import apiClient from '../../../../../api/client';

/**
 * Complete Inventory Service - All functionality in one place
 * Handles all inventory-related API calls and data operations
 */
const inventoryService = {
  // ==========================================
  // CORE INVENTORY STOCK MANAGEMENT
  // ==========================================

  /**
   * Get all inventory stock data
   * @returns {Promise<Object>} Stock data with items and current levels
   */
  getStock: async () => {
    try {
      console.log('📦 InventoryService: Fetching stock data...');
      const response = await apiClient.get('/api/v1/inventory/stock');
      console.log('📦 InventoryService: Stock data loaded:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ InventoryService: Error fetching stock:', error);
      throw error;
    }
  },

  /**
   * Get pre-made items (menu items without recipes)
   * @returns {Promise<Object>} Pre-made items data
   */
  getPreMadeItems: async () => {
    try {
      console.log('📦 InventoryService: Fetching pre-made items...');
      const response = await apiClient.get('/api/v1/inventory/pre-made-items');
      console.log('📦 InventoryService: Pre-made items loaded:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ InventoryService: Error fetching pre-made items:', error);
      throw error;
    }
  },

  /**
   * Get recipe items (menu items with ingredient recipes)
   * @returns {Promise<Object>} Recipe items data
   */
    getRecipeItems: async () => {
    try {
      console.log('🍽️ InventoryService: Fetching recipe items...');
      const response = await apiClient.get('/api/v1/inventory/recipe-items');
      console.log('✅ InventoryService: Recipe items loaded:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ InventoryService: Error fetching recipe items:', error);
      throw error;
    }
  },

  saveItemSetup: async (itemData) => {
    try {
      console.log('💾 InventoryService: Saving item setup...');
      const response = await apiClient.post('/api/v1/inventory/setup', {
        stockData: [itemData]
      });
      console.log('✅ InventoryService: Item setup saved:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ InventoryService: Error saving item setup:', error);
      throw error;
    }
  },

  /**
   * Get consolidated stock setup data
   * @returns {Promise<Object>} All stock setup data
   */
  getStockSetupData: async () => {
    try {
      console.log('📦 InventoryService: Fetching consolidated stock setup data...');
      const [menuItems, ingredients, stockData, preMadeItems, recipeItems] = await Promise.allSettled([
        apiClient.get('/api/v1/pos/pos-items'),
        apiClient.get('/api/v1/ingredients'),
        apiClient.get('/api/v1/inventory/stock'),
        inventoryService.getPreMadeItems(),
        inventoryService.getRecipeItems()
      ]);

      return {
        success: true,
        data: {
          menuItems: menuItems.status === 'fulfilled' ? menuItems.value.data : { data: [] },
          ingredients: ingredients.status === 'fulfilled' ? ingredients.value.data : { data: [] },
          stockData: stockData.status === 'fulfilled' ? stockData.value.data : { data: [] },
          preMadeItems: preMadeItems.status === 'fulfilled' ? preMadeItems.value.data : { data: [] },
          recipeItems: recipeItems.status === 'fulfilled' ? recipeItems.value.data : { data: [] }
        }
      };
    } catch (error) {
      console.error('❌ InventoryService: Error fetching stock setup data:', error);
      throw error;
    }
  },

  /**
   * Reset all stock levels for new day (set to maximum quantities)
   * @returns {Promise<Object>} Reset operation result
   */
  resetStockForNewDay: async () => {
    try {
      console.log('🔄 InventoryService: Resetting stock for new day...');
      const response = await apiClient.post('/api/v1/inventory/reset-new-day');
      console.log('✅ InventoryService: Stock reset completed:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ InventoryService: Error resetting stock:', error);
      throw error;
    }
  },

  /**
   * Get categories with inventory information
   * @returns {Promise<Object>} Categories with stock stats
   */
  getCategories: async () => {
    try {
      console.log('📂 InventoryService: Fetching categories...');
      const response = await apiClient.get('/api/v1/inventory/categories');
      console.log('📂 InventoryService: Categories loaded:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ InventoryService: Error fetching categories:', error);
      throw error;
    }
  },

  /**
   * Save stock setup data (batch operation)
   * @param {Object} payload - { stockData: Array of stock items }
   * @returns {Promise<Object>} Save response
   */
  saveSetup: async (payload) => {
    try {
      console.log('💾 InventoryService: Saving stock setup...', payload);
      const response = await apiClient.post('/api/v1/inventory/setup', payload);
      console.log('✅ InventoryService: Setup saved successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ InventoryService: Error saving setup:', error);
      throw error;
    }
  },

  /**
   * Save consolidated stock setup data (both pre-made and recipe)
   * @param {Object} setupData - { preMadeItems: Array, recipeItems: Array }
   * @returns {Promise<Object>} Save response
   */
  saveStockSetupData: async (setupData) => {
    try {
      console.log('💾 InventoryService: Saving consolidated stock setup...');
      const { preMadeItems, recipeItems } = setupData;
      const savePromises = [];

      // Save pre-made items stock
      if (preMadeItems?.length > 0) {
        savePromises.push(
          apiClient.post('/api/v1/inventory/setup', { stockData: preMadeItems })
        );
      }

      // Save recipe items
      if (recipeItems?.length > 0) {
        savePromises.push(
          apiClient.post('/api/v1/inventory/recipes/batch', { recipes: recipeItems })
        );
      }

      const results = await Promise.allSettled(savePromises);
      
      return {
        success: results.every(r => r.status === 'fulfilled'),
        message: 'Stock setup data saved successfully',
        results
      };
    } catch (error) {
      console.error('❌ InventoryService: Error saving stock setup:', error);
      throw error;
    }
  },

  // ==========================================
  // STOCK ADJUSTMENTS
  // ==========================================

  /**
   * Create stock adjustment
   * @param {Object} adjustmentData - { itemId, physicalCount, reason, notes }
   * @returns {Promise<Object>} Adjustment response
   */
  createAdjustment: async (adjustmentData) => {
    try {
      console.log('📝 InventoryService: Creating stock adjustment...', adjustmentData);
      const response = await apiClient.post('/api/v1/inventory/adjust', adjustmentData);
      console.log('✅ InventoryService: Adjustment created:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ InventoryService: Error creating adjustment:', error);
      throw error;
    }
  },

  /**
   * Get stock adjustment history
   * @param {Object} params - { limit, offset, dateFrom, dateTo }
   * @returns {Promise<Object>} History data
   */
  getAdjustmentHistory: async (params = {}) => {
    try {
      console.log('📋 InventoryService: Fetching adjustment history...');
      const queryParams = new URLSearchParams(params).toString();
      const url = `/api/v1/inventory/history${queryParams ? `?${queryParams}` : ''}`;
      const response = await apiClient.get(url);
      console.log('✅ InventoryService: History fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ InventoryService: Error fetching history:', error);
      throw error;
    }
  },

  // ==========================================
  // EXPORT & ALERTS
  // ==========================================

  /**
   * Export stock data
   * @returns {Promise<Object>} Export data
   */
  exportStock: async () => {
    try {
      console.log('📊 InventoryService: Exporting stock data...');
      const response = await apiClient.get('/api/v1/inventory/export');
      console.log('✅ InventoryService: Stock exported:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ InventoryService: Error exporting stock:', error);
      throw error;
    }
  },

  /**
   * Get low stock alerts for finished goods
   * @returns {Promise<Object>} Low stock alerts
   */
  getLowStockAlerts: async () => {
    try {
      console.log('⚠️ InventoryService: Fetching low stock alerts...');
      const response = await apiClient.get('/api/v1/inventory/alerts');
      console.log('✅ InventoryService: Alerts fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ InventoryService: Error fetching alerts:', error);
      throw error;
    }
  },

  /**
   * Get combined low stock alerts (both finished goods and ingredients)
   */
  getAllLowStockAlerts: async () => {
    try {
      console.log('🚨 InventoryService: Fetching all low stock alerts...');
      
      const [finishedGoodsAlerts, ingredientAlerts] = await Promise.allSettled([
        apiClient.get('/api/v1/inventory/alerts'),
        apiClient.get('/api/v1/ingredients/alerts')
      ]);

      const combinedAlerts = {
        finished_goods: finishedGoodsAlerts.status === 'fulfilled' && finishedGoodsAlerts.value?.data?.success 
          ? finishedGoodsAlerts.value.data.data : [],
        ingredients: ingredientAlerts.status === 'fulfilled' && ingredientAlerts.value?.data?.success
          ? ingredientAlerts.value.data.data : []
      };

      const totalAlerts = combinedAlerts.finished_goods.length + combinedAlerts.ingredients.length;

      console.log('✅ InventoryService: Combined alerts loaded:', totalAlerts);
      return {
        success: true,
        data: combinedAlerts,
        total: totalAlerts,
        message: `Found ${totalAlerts} low stock alerts`
      };
      
    } catch (error) {
      console.error('❌ InventoryService: Error fetching combined alerts:', error);
      throw error;
    }
  },

  // ==========================================
  // POS INTEGRATION
  // ==========================================

  /**
   * Record stock movement for POS integration
   * @param {Object} movementData - { item_id, movement_type, quantity_change, reference_id, reason }
   * @returns {Promise<Object>} Movement response
   */
  recordMovement: async (movementData) => {
    try {
      console.log('📝 InventoryService: Recording stock movement...', movementData);
      
      // Convert to stock adjustment format for the backend
      const adjustmentData = {
        itemId: movementData.item_id,
        physicalCount: movementData.quantity_change,
        reason: movementData.reason || 'pos_sale',
        notes: `${movementData.movement_type} - ${movementData.reason || 'POS transaction'}`
      };
      
      const response = await apiClient.post('/api/v1/inventory/adjust', adjustmentData);
      console.log('✅ InventoryService: Movement recorded:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ InventoryService: Error recording movement:', error);
      throw error;
    }
  },

  // ==========================================
  // UTILITY METHODS
  // ==========================================

  /**
   * Set initial stock for an item (legacy method for compatibility)
   * @param {number} itemId - Item ID
   * @param {Object} stockData - Stock configuration
   * @returns {Promise<Object>} Response
   * @deprecated Use saveSetup instead
   */
  setInitialStock: async (itemId, stockData) => {
    console.warn('⚠️ setInitialStock is deprecated, use saveSetup instead');
    return inventoryService.saveSetup({
      stockData: [{
        item_id: itemId,
        ...stockData
      }]
    });
  },

  /**
   * Bulk update stock levels
   * @param {Array} updates - Array of { item_id, current_quantity, min_quantity, etc. }
   * @returns {Promise<Object>} Update response
   */
  bulkUpdateStock: async (updates) => {
    try {
      console.log('🔄 InventoryService: Bulk updating stock...', updates.length, 'items');
      const response = await inventoryService.saveSetup({ stockData: updates });
      console.log('✅ InventoryService: Bulk update completed');
      return response;
    } catch (error) {
      console.error('❌ InventoryService: Error in bulk update:', error);
      throw error;
    }
  },

  /**
   * Get stock level for specific item
   * @param {number} itemId - Item ID
   * @returns {Promise<Object>} Stock data for item
   */
  getItemStock: async (itemId) => {
    try {
      console.log('🔍 InventoryService: Fetching stock for item:', itemId);
      const stockResponse = await inventoryService.getStock();
      const itemStock = stockResponse.data.find(item => item.item_id === itemId);
      
      if (!itemStock) {
        return {
          item_id: itemId,
          current_quantity: 0,
          min_quantity: 0,
          max_quantity: 100,
          unit_of_measure: 'pcs',
          cost_per_unit: 0,
          stock_status: 'out_of_stock'
        };
      }
      
      return itemStock;
    } catch (error) {
      console.error('❌ InventoryService: Error fetching item stock:', error);
      throw error;
    }
  },

  /**
   * Check if item is in stock
   * @param {number} itemId - Item ID
   * @param {number} requiredQuantity - Required quantity (default: 1)
   * @returns {Promise<boolean>} True if sufficient stock available
   */
  checkStock: async (itemId, requiredQuantity = 1) => {
    try {
      const itemStock = await inventoryService.getItemStock(itemId);
      return itemStock.current_quantity >= requiredQuantity;
    } catch (error) {
      console.error('❌ InventoryService: Error checking stock:', error);
      return false; // Assume no stock on error
    }
  },

  /**
   * Validate stock data before saving
   * @param {Array} stockData - Array of stock items to validate
   * @returns {Object} { isValid: boolean, errors: Array }
   */
  validateStockData: (stockData) => {
    const errors = [];
    
    if (!Array.isArray(stockData)) {
      errors.push('Stock data must be an array');
      return { isValid: false, errors };
    }
    
    stockData.forEach((item, index) => {
      if (!item.item_id) {
        errors.push(`Item ${index + 1}: Missing item_id`);
      }
      
      if (item.current_quantity < 0) {
        errors.push(`Item ${index + 1}: Current quantity cannot be negative`);
      }
      
      if (item.min_quantity < 0) {
        errors.push(`Item ${index + 1}: Min quantity cannot be negative`);
      }
      
      if (item.max_quantity < item.min_quantity) {
        errors.push(`Item ${index + 1}: Max quantity must be greater than min quantity`);
      }
      
      if (item.cost_per_unit < 0) {
        errors.push(`Item ${index + 1}: Cost per unit cannot be negative`);
      }
    });
    
    return {
      isValid: errors.length === 0,
      errors
    };
  },

  /**
   * Calculate recipe cost
   * @param {Object} recipe - Recipe with ingredients
   * @param {Array} ingredients - Available ingredients
   * @returns {number} Total recipe cost
   */
  calculateRecipeCost: (recipe, ingredients) => {
    if (!recipe.ingredients || !ingredients.length) return 0;
    
    return recipe.ingredients.reduce((total, recipeIngredient) => {
      const ingredient = ingredients.find(ing => ing.ingredient_id === recipeIngredient.ingredient_id);
      if (!ingredient) return total;
      
      const ingredientCost = ingredient.cost_per_unit || 0;
      const quantity = recipeIngredient.quantity_per_yield || 0;
      
      return total + (ingredientCost * quantity);
    }, 0);
  },

  /**
   * Validate recipe stock availability
   * @param {Object} recipe - Recipe to validate
   * @param {Array} ingredients - Available ingredients
   * @returns {Array} Array of warning messages
   */
  validateRecipeStock: (recipe, ingredients) => {
    const warnings = [];
    
    if (!recipe.ingredients) return warnings;
    
    recipe.ingredients.forEach(recipeIngredient => {
      const ingredient = ingredients.find(ing => ing.ingredient_id === recipeIngredient.ingredient_id);
      if (!ingredient) {
        warnings.push(`Ingredient not found: ${recipeIngredient.ingredient_name}`);
        return;
      }
      
      const required = recipeIngredient.quantity_per_yield || 0;
      const available = ingredient.current_stock || 0;
      
      if (available < required) {
        warnings.push(`Insufficient ${ingredient.name}: need ${required}, have ${available}`);
      }
    });
    
    return warnings;
  },

  // ==========================================
  // PRODUCTION LOG METHODS
  // ==========================================

  /**
   * Get production records
   * @param {Object} filters - { dateFrom, dateTo, productId }
   * @returns {Promise<Object>} Production records
   */
  getProductionRecords: async (filters = {}) => {
    try {
      console.log('🏭 InventoryService: Fetching production records...', filters);
      const params = new URLSearchParams();
      if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.append('dateTo', filters.dateTo);
      if (filters.productId) params.append('productId', filters.productId);

      const response = await apiClient.get(`/api/v1/inventory/production?${params}`);
      console.log('✅ InventoryService: Production records loaded:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ InventoryService: Error fetching production records:', error);
      throw error;
    }
  },

  /**
   * Create production record with enhanced ingredient tracking
   * @param {Object} data - Production record data
   * @returns {Promise<Object>} Created record response
   */
  createProductionRecord: async (data) => {
    try {
      console.log('🆕 InventoryService: Creating enhanced production record:', data);
      const response = await apiClient.post('/api/v1/inventory/production', data);
      console.log('✅ InventoryService: Production record created with ingredient tracking:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ InventoryService: Error creating production record:', error);
      throw error;
    }
  },

  /**
   * Update production record
   * @param {number} id - Record ID
   * @param {Object} data - Updated data
   * @returns {Promise<Object>} Update response
   */
  updateProductionRecord: async (id, data) => {
    try {
      console.log('✏️ InventoryService: Updating production record:', id);
      const response = await apiClient.put(`/api/v1/inventory/production/${id}`, data);
      console.log('✅ InventoryService: Production record updated:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ InventoryService: Error updating production record:', error);
      throw error;
    }
  },

  /**
   * Delete production record
   * @param {number} id - Record ID
   * @returns {Promise<Object>} Delete response
   */
  deleteProductionRecord: async (id) => {
    try {
      console.log('🗑️ InventoryService: Deleting production record:', id);
      const response = await apiClient.delete(`/api/v1/inventory/production/${id}`);
      console.log('✅ InventoryService: Production record deleted:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ InventoryService: Error deleting production record:', error);
      throw error;
    }
  },

  // ==========================================
  // RECIPE MANAGEMENT
  // ==========================================

  /**
   * Get recipe for an item
   * @param {number} itemId - Item ID
   * @returns {Promise<Object>} Recipe data
   */
  getRecipeByItem: async (itemId) => {
    try {
      console.log('📚 InventoryService: Fetching recipe for item:', itemId);
      const response = await apiClient.get(`/api/v1/inventory/recipes/${itemId}`);
      console.log('✅ InventoryService: Recipe loaded:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ InventoryService: Error fetching recipe:', error);
      throw error;
    }
  },

  /**
   * Get recipe with ingredients for production planning
   * @param {number} itemId - Item ID
   * @returns {Promise<Object>} Recipe with ingredients data
   */
  getRecipeWithIngredients: async (itemId) => {
    try {
      console.log('🧾 InventoryService: Fetching recipe with ingredients for item:', itemId);
      const response = await apiClient.get(`/api/v1/inventory/recipes/${itemId}/ingredients`);
      console.log('✅ InventoryService: Recipe with ingredients loaded:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ InventoryService: Error fetching recipe with ingredients:', error);
      throw error;
    }
  },

  /**
   * Save recipe for menu item
   * @param {Object} recipeData - Recipe data with ingredients
   * @returns {Promise<Object>} Save response
   */
  saveRecipe: async (recipeData) => {
    try {
      console.log('💾 InventoryService: Saving recipe...', recipeData);
      const response = await apiClient.post('/api/v1/inventory/recipes', recipeData);
      console.log('✅ InventoryService: Recipe saved:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ InventoryService: Error saving recipe:', error);
      throw error;
    }
  },

  /**
   * Update recipe
   * @param {number} itemId - Item ID
   * @param {Object} recipeData - Updated recipe data
   * @returns {Promise<Object>} Update response
   */
  updateRecipe: async (itemId, recipeData) => {
    try {
      console.log('✏️ InventoryService: Updating recipe for item:', itemId);
      const response = await apiClient.put(`/api/v1/inventory/recipes/${itemId}`, recipeData);
      console.log('✅ InventoryService: Recipe updated:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ InventoryService: Error updating recipe:', error);
      throw error;
    }
  },

  /**
   * Delete recipe
   * @param {number} itemId - Item ID
   * @returns {Promise<Object>} Delete response
   */
  deleteRecipe: async (itemId) => {
    try {
      console.log('🗑️ InventoryService: Deleting recipe for item:', itemId);
      const response = await apiClient.delete(`/api/v1/inventory/recipes/${itemId}`);
      console.log('✅ InventoryService: Recipe deleted:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ InventoryService: Error deleting recipe:', error);
      throw error;
    }
  },

  // ==========================================
  // WASTAGE LOG METHODS
  // ==========================================

  /**
   * Get wastage records for finished goods
   * @param {Object} filters - { dateFrom, dateTo, productId, reason }
   * @returns {Promise<Object>} Wastage records
   */
  getWastageRecords: async (filters = {}) => {
    try {
      console.log('📋 InventoryService: Fetching wastage records...', filters);
      const params = new URLSearchParams();
      if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.append('dateTo', filters.dateTo);
      if (filters.productId) params.append('productId', filters.productId);
      if (filters.reason) params.append('reason', filters.reason);

      const response = await apiClient.get(`/api/v1/inventory/wastage?${params}`);
      console.log('✅ InventoryService: Wastage records loaded:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ InventoryService: Error fetching wastage records:', error);
      throw error;
    }
  },

  /**
   * Create wastage record - supports both finished goods and ingredients
   * @param {Object} data - Wastage record data
   * @returns {Promise<Object>} Created record response
   */
  createWastageRecord: async (data) => {
    try {
      console.log('🆕 InventoryService: Creating wastage record:', data);
      
      // Determine endpoint based on wastage type
      let endpoint = '/api/v1/inventory/wastage'; // Default for finished goods
      
      if (data.ingredient_id) {
        // This is ingredient wastage
        endpoint = '/api/v1/ingredients/wastage';
      }
      
      const response = await apiClient.post(endpoint, data);
      console.log('✅ InventoryService: Wastage record created:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ InventoryService: Error creating wastage record:', error);
      throw error;
    }
  },

  /**
   * Get combined wastage records (both finished goods and ingredients)
   * @param {Object} filters - Filter options
   * @returns {Promise<Object>} Combined wastage records
   */
  getCombinedWastageRecords: async (filters = {}) => {
    try {
      console.log('📋 InventoryService: Fetching combined wastage records...', filters);
      
      const params = new URLSearchParams();
      if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.append('dateTo', filters.dateTo);
      if (filters.reason) params.append('reason', filters.reason);
      if (filters.limit) params.append('limit', filters.limit);

      // Fetch both finished goods and ingredient wastage in parallel
      const [finishedGoodsResponse, ingredientsResponse] = await Promise.allSettled([
        apiClient.get(`/api/v1/inventory/wastage?${params}`),
        apiClient.get(`/api/v1/ingredients/wastage?${params}`)
      ]);

      // Process results
      const finishedGoods = finishedGoodsResponse.status === 'fulfilled' && finishedGoodsResponse.value?.data?.success
        ? finishedGoodsResponse.value.data.data.map(record => ({ ...record, waste_type: 'finished_goods' }))
        : [];

      const ingredients = ingredientsResponse.status === 'fulfilled' && ingredientsResponse.value?.data?.success
        ? ingredientsResponse.value.data.data.map(record => ({ ...record, waste_type: 'ingredients' }))
        : [];

      // Combine and sort by date
      const combinedRecords = [...finishedGoods, ...ingredients]
        .sort((a, b) => new Date(b.date || b.created_at) - new Date(a.date || a.created_at));

      console.log('✅ InventoryService: Combined wastage records loaded:', combinedRecords.length);
      
      return {
        success: true,
        data: combinedRecords,
        summary: {
          total: combinedRecords.length,
          finished_goods: finishedGoods.length,
          ingredients: ingredients.length
        }
      };
    } catch (error) {
      console.error('❌ InventoryService: Error fetching combined wastage records:', error);
      throw error;
    }
  },

  /**
   * Update wastage record
   * @param {number} id - Record ID
   * @param {Object} data - Updated data
   * @returns {Promise<Object>} Update response
   */
  updateWastageRecord: async (id, data) => {
    try {
      console.log('✏️ InventoryService: Updating wastage record:', id);
      const response = await apiClient.put(`/api/v1/inventory/wastage/${id}`, data);
      console.log('✅ InventoryService: Wastage record updated:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ InventoryService: Error updating wastage record:', error);
      throw error;
    }
  },

  /**
   * Delete wastage record
   * @param {number} id - Record ID
   * @returns {Promise<Object>} Delete response
   */
  deleteWastageRecord: async (id) => {
    try {
      console.log('🗑️ InventoryService: Deleting wastage record:', id);
      const response = await apiClient.delete(`/api/v1/inventory/wastage/${id}`);
      console.log('✅ InventoryService: Wastage record deleted:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ InventoryService: Error deleting wastage record:', error);
      throw error;
    }
  },

  // ==========================================
  // ANALYTICS & DASHBOARD
  // ==========================================

  /**
   * Get comprehensive dashboard data including ingredients
   * @returns {Promise<Object>} Dashboard data
   */
  getEnhancedDashboardData: async () => {
    try {
      console.log('📊 InventoryService: Fetching enhanced dashboard data...');
      
      // Fetch multiple data sources in parallel
      const [
        stockResponse,
        ingredientsResponse,
        alertsResponse,
        productionResponse,
        wastageResponse
      ] = await Promise.allSettled([
        apiClient.get('/api/v1/inventory/stock'),
        apiClient.get('/api/v1/ingredients'),
        inventoryService.getAllLowStockAlerts(),
        apiClient.get('/api/v1/inventory/production?limit=10'),
        inventoryService.getCombinedWastageRecords({ limit: 10 })
      ]);

      const dashboardData = {
        stock: stockResponse.status === 'fulfilled' ? stockResponse.value.data : { data: [] },
        ingredients: ingredientsResponse.status === 'fulfilled' ? ingredientsResponse.value.data : { data: [] },
        alerts: alertsResponse.status === 'fulfilled' ? alertsResponse.value : { data: { finished_goods: [], ingredients: [] }, total: 0 },
        recentProduction: productionResponse.status === 'fulfilled' ? productionResponse.value.data : { data: [] },
        recentWastage: wastageResponse.status === 'fulfilled' ? wastageResponse.value : { data: [] }
      };

      console.log('✅ InventoryService: Enhanced dashboard data loaded');
      return { success: true, data: dashboardData };
      
    } catch (error) {
      console.error('❌ InventoryService: Error fetching enhanced dashboard data:', error);
      throw error;
    }
  },

  // ==========================================
  // SYSTEM HEALTH CHECK
  // ==========================================

  /**
   * Check system health including ingredients
   * @returns {Promise<Object>} Health check results
   */
  checkSystemHealth: async () => {
    try {
      console.log('🔍 InventoryService: Checking system health...');
      
      const healthChecks = await Promise.allSettled([
        apiClient.get('/api/v1/inventory/stock'),
        apiClient.get('/api/v1/ingredients'),
        apiClient.get('/api/v1/inventory/production'),
        apiClient.get('/api/v1/inventory/wastage')
      ]);

      const results = {
        inventory: healthChecks[0].status === 'fulfilled',
        ingredients: healthChecks[1].status === 'fulfilled',
        production: healthChecks[2].status === 'fulfilled',
        wastage: healthChecks[3].status === 'fulfilled'
      };

      const allHealthy = Object.values(results).every(status => status);
      
      console.log('✅ InventoryService: System health check completed:', results);
      return {
        success: true,
        data: results,
        healthy: allHealthy,
        message: allHealthy ? 'All systems operational' : 'Some systems have issues'
      };
      
    } catch (error) {
      console.error('❌ InventoryService: Health check failed:', error);
      return {
        success: false,
        healthy: false,
        message: 'Health check failed',
        error: error.message
      };
    }
  },

  /**
   * Get menu item stock availability
   * @param {number} itemId - Menu item ID
   * @returns {Promise<Object>} Stock availability data
   */
  getMenuItemStock: async (itemId) => {
    try {
      console.log('📦 Getting stock for menu item:', itemId);
      const response = await apiClient.get(`/api/v1/inventory/menu-item-stock/${itemId}`);
      return response.data;
    } catch (error) {
      console.error('❌ Error getting menu item stock:', error);
      throw error;
    }
  },

   /**
   * Check if a menu item has enough ingredients for the requested quantity
   * @param {Object} data - { item_id, quantity }
   * @returns {Promise<Object>} Availability response
   */
  checkMenuItemAvailability: async (data) => {
    try {
      console.log('🔍 InventoryService: Checking menu item availability...', data);
      // Use the correct endpoint matching the backend route
      const response = await apiClient.post('/api/v1/inventory/check-menu-item-availability', data);
      console.log('✅ InventoryService: Availability check result:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ InventoryService: Error checking availability:', error);
      // Return a default response instead of throwing to prevent UI errors
      return {
        success: true,
        available: true,
        shortages: [],
        max_quantity: 999
      };
    }
  },

  //Production Control 
  createProductionTransaction: async (data) => {
    try {
      console.log('🏭 Creating production transaction:', data);
      const response = await apiClient.post('/api/v1/inventory/production-transaction', data);
      return response.data;
    } catch (error) {
      console.error('❌ Error creating production transaction:', error);
      throw error;
    }
  },

  revertProductionTransaction: async (data) => {
    try {
      console.log('↩️ Reverting production transaction:', data);
      const response = await apiClient.post('/api/v1/inventory/revert-production', data);
      return response.data;
    } catch (error) {
      console.error('❌ Error reverting production:', error);
      throw error;
    }
  }
};

export default inventoryService;